/* ============================================================
   Trust Carousel — JS
   rAF + translateX infinite marquee. No dependencies.
   Tablet (750–989px): CSS freezes transform; JS still runs
   but CSS mask/transform override keeps it static visually.
   ============================================================ */
(() => {
  const TABLET_MIN = 769;
  const TABLET_MAX = 1199;
  const RESIZE_DEBOUNCE = 150;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isTablet = () => {
    const w = window.innerWidth;
    return w >= TABLET_MIN && w <= TABLET_MAX;
  };

  function initSection(section) {
    const viewport = section.querySelector('.trust-carousel__viewport');
    const track    = section.querySelector('.trust-carousel__track');
    if (!viewport || !track) return () => {};

    const speedPxS = parseFloat(section.dataset.speed) || 40; // px/second

    let position   = 0;
    let halfWidth  = 0;
    let animId     = null;
    let running    = false;

    function measure() {
      track.style.transform = '';
      void track.scrollWidth;
      halfWidth = track.scrollWidth / 2;
    }

    function wrap() {
      if (halfWidth === 0) return;
      if (position <= -halfWidth) position += halfWidth;
      if (Math.abs(position) > halfWidth * 2) position = position % halfWidth;
    }

    function tick() {
      if (!prefersReducedMotion() && !isTablet()) {
        position -= speedPxS / 60;
        wrap();
        track.style.transform = 'translateX(' + position + 'px)';
      }
      animId = requestAnimationFrame(tick);
    }

    function start() {
      if (running) return;
      running = true;
      measure();
      position = 0;
      if (!prefersReducedMotion()) animId = requestAnimationFrame(tick);
    }

    function stop() {
      running = false;
      if (animId) { cancelAnimationFrame(animId); animId = null; }
      track.style.transform = '';
    }

    function remeasure() {
      const prev = halfWidth;
      measure();
      if (prev > 0 && halfWidth > 0) position = (position / prev) * halfWidth;
      wrap();
      if (!isTablet()) track.style.transform = 'translateX(' + position + 'px)';
      else track.style.transform = '';
    }

    /* IntersectionObserver: pause off-screen */
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (!running) start();
          else if (!animId && !prefersReducedMotion())
            animId = requestAnimationFrame(tick);
        } else {
          if (animId) { cancelAnimationFrame(animId); animId = null; }
        }
      });
    }, { threshold: 0.05 });
    observer.observe(section);

    /* Resize */
    let resizeTimer = null;
    function onResize() {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => requestAnimationFrame(remeasure), RESIZE_DEBOUNCE);
    }
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    return function destroy() {
      stop();
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }

  const destroyMap = new Map();

  function boot() {
    document.querySelectorAll('.trust-carousel').forEach(section => {
      const id = section.id;
      if (destroyMap.has(id)) destroyMap.get(id)();
      destroyMap.set(id, initSection(section));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', e => {
    const section = e.target.querySelector('.trust-carousel');
    if (!section) return;
    const id = section.id;
    if (destroyMap.has(id)) destroyMap.get(id)();
    destroyMap.set(id, initSection(section));
  });

  document.addEventListener('shopify:section:unload', e => {
    const section = e.target.querySelector('.trust-carousel');
    if (!section) return;
    const id = section.id;
    if (destroyMap.has(id)) { destroyMap.get(id)(); destroyMap.delete(id); }
  });
})();
